import { ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { LoaderCircle, Info, Scale, ClipboardCheck, TrendingDown, ShieldCheck, ChevronDown, ChevronUp, Search, ArrowLeftRight, Download, ListFilter } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  primaryText,
  secondaryText,
  tertiaryText,
  semiBoldText,
  mediumText,
  smallTextSize,
  largeTextSize,
  redTextColor,
  greenTextColor,
  borderPrimary,
  cardBg,
  canvasBg,
  placeholderPrimary,
  rounded,
  iconBgBlue,
  iconBgGreen,
  iconBgRed,
  iconBgPurple,
} from '../../../styling/stylingHelper';
import { StatCard } from '../../ui/StatCard';
import { CardWithHeader } from '../../ui/CardWithHeader';
import { NoDataInPeriod } from '../../ui/NoDataInPeriod';
import { SlideOverShell } from '../../ui/slideOverParts';
import { TabbedSections } from '../../ui/TabbedSections';
import { TableComponent, TableComponentProps } from '../../ui/TableComponent';
import { ResponsiveTable, MobileCardField, MobileCardList } from '../../shared/mobile/ResponsiveTable';
import { FavoriteStar } from '../../ui/FavoriteStar';
import { SupplierLogo } from '../../ui/SupplierLogo';
import { Platform } from '../../../types/Platforms';
import ProductSetupModal from '../costPrice/new/CostPrice/ProductSetupModal';
import CostPriceDetailModal from '../costPrice/new/CostPrice/CostPriceDetailModal';
import { currencyFormatter, formatPlatformName } from '../../../services/helperService';
import { updateRestaurantFavoriteProducts } from '../../../services/restaurant/restaurantService';
import { getCategoryDisplayName } from '../../orderhistory/categoryUtils';
import { useOrderHistory } from '../../../stores/appOrderHistoryStore';
import { useRevenueHistory } from '../../../stores/appRevenueHistoryStore';
import { useCostPriceStore } from '../../../stores/appCostPriceStore';
import { useSubrecipesStore } from '../../../stores/appSubrecipesStore';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { useInventoryStore } from '../../../stores/appInventoryStore';
import { useAppUserDefaultStore, saveLeakageQtyUnitSetting, loadLeakageQtyUnitSetting } from '../../../stores/appUserDefaultStore';
import {
  useLoadOrderHistory,
  useLoadRevenueHistory,
  useLoadCostPrice,
  useLoadSubrecipes,
} from '../../../utils/dataLoadingUtils';
import { resolveWastePercentage } from '../../../utils/wasteUtils';
import { cachedReportValue } from '../../../utils/reports/reportMemoCache';
import { ConsumptionCustomRow, inventoryKey } from '../../../utils/reports/consumptionReport';
import { productKey } from '../../../utils/reports/shared';
import { getInventoryPracticeDataset, isInventoryPracticeReference, isCustomerInventoryPractice, useInventoryPracticeDataset } from '../../onboarding/practice/inventoryPractice';
import { isPracticeId } from '../../onboarding/practice/practiceGuards';
import { PracticeScopeProvider } from '../../onboarding/practice/practiceScope';
import { usePracticeDataset } from '../../onboarding/practice/practiceDataset';
import { buildLeakageTourSnapshot, leakageSetupDish, leakageWalkthroughRowUid, publishLeakageTourState } from '../../onboarding/inventoryValues';
import { buildPerUnitNeeds } from '../../../utils/reports/utilizationReport';
import { CategoryMode } from './ValueByCategoryCard';
import { RankedValueBars, RankedBarRow } from './RankedValueBars';
import { inventorySelectCls as selectCls } from './ComparedAgainstSlot';
import LeakageProductDrawer, { LeakageDishUsage, LinkedOrderProduct } from './LeakageProductDrawer';
import { attachUsageCostPriceIds, UsageWithCostPrice } from './leakageUsageCostPrice';
import { buildProductSetupsIndex, mergeSetupsIntoUsage, UsageWithSetup } from './leakageProductSetups';
import {
  hasComparableQty,
  leakageUsagePercentage,
  makeLeakageQtyFormat,
  LeakageUnitMode,
  anchorKeyOf,
  isRowFavorite,
  toggleFavoriteKeys,
  rowMatchesSearch,
} from './leakagePoolDisplay';
import ReLinkInventoryProductModal, { RelinkTarget } from './ReLinkInventoryProductModal';
import { buildLeakageCsv, leakageCsvFilename, LeakageCsvEntry } from './leakageCsv';
import { buildLeakageDetailedCsv, LeakageDetailedCsvEntry } from './leakageDetailedCsv';
import { downloadCsv } from '../../../utils/reports/utilizationCsv';
import { groupCountsIntoSessions } from './inventoryOverviewUtils';
import {
  grayBadgeClass as grayBadge,
} from './inventoryReviewShared';
import {
  computeInventoryVariance,
  computeInventoryVarianceAll,
  buildVarianceBaselineOptions,
  buildPerDishUsage,
  UNATTRIBUTED_KEY,
  VarianceRow,
  VariancePoolMember,
  VarianceSectionRow,
  VarianceExcludedProduct,
  VarianceConfidence,
  CanonicalCountUnit,
} from '../../../utils/reports/inventoryVariance';

interface LeakageTabProps {
  restaurantId: string;
  /** The page's shared count selection (null = All inventories / Latest). */
  selection: { groupId: string | null; sessionId: string | null; endSessionKey?: string; baselineSessionKey?: string };
  onBaselineChange?: (key: string) => void;
  /** Same gate as the page's Count Review export (`inventory_count_export` / impersonation). */
  canExport: boolean;
  /** Restaurant name, used for the export filename only. */
  restaurantName: string;
}

/** Common totals shape shared by a single-group report and the all-groups combined roll-up. */
interface LeakageTotals {
  startTotalValue: number;
  purchasedTotalValue: number;
  neededTotalValue: number;
  adjustmentValue: number;
  expectedTotalValue: number;
  countedTotalValue: number;
  varianceTotalValue: number;
  confidenceScore: number;
  linkedProductCount: number;
  totalProductCount: number;
}

/** A reconciled row, optionally carrying its group name (all-groups combined view). */
type LeakageRow = VarianceRow & { groupName?: string | null };

/** Product-table display row: a reconciled row, or — when a pool is expanded — one of its
 *  members rendered in the SAME columns so the facts line up under the aggregates. */
type LeakageDisplayRow =
  | { kind: 'row'; row: LeakageRow }
  | { kind: 'member'; member: VariancePoolMember; parent: LeakageRow };

/** Normalised view fed to the breakdown cards / product table (single group OR all groups). */
interface LeakageView {
  isAll: boolean;
  rows: LeakageRow[];
  sections: VarianceSectionRow[];
  customItems: ConsumptionCustomRow[];
  excluded: VarianceExcludedProduct[];
  unmeasurableProductNames: string[];
  unattributedVarianceValue: number;
  varianceTotalValue: number;
  unattributedShare: number;
}

/** A component-computed category aggregate (works for both single + all-groups views). */
interface LeakageCatRow {
  name: string;
  expectedValue: number;
  countedValue: number;
  varianceValue: number;
  variancePct: number | null;
  productCount: number;
}

// --- module helpers -------------------------------------------------------

/** Card treatment shared by every block of the tab (utilization page's soft card). */
const softCard = 'shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040]';

/** How many bars a breakdown chart shows before "see all" takes over. */
const TOP_BARS = 4;

const money = (v: number): string => currencyFormatter.format(v);
const pctText = (v: number | null): string => (v === null ? '—' : `${(v * 100).toFixed(0)}%`);

/** Tone class for a leakage € value (loss = red). */
const eurTone = (v: number | null): string => (v !== null && v < 0 ? redTextColor : primaryText);

/** Leakage € cell: dash + tooltip when there's no usable price, else money (red on loss). */
function renderEur(t: TFunction, v: number | null): ReactNode {
  if (v === null)
    return (
      <span title={t('inventory.leakage.noPriceTooltip')} className={tertiaryText}>
        —
      </span>
    );
  return <span className={cn(semiBoldText, eurTone(v))}>{money(v)}</span>;
}

/** Drawer selection id — the product key alone repeats across groups in the All view. */
const rowUid = (r: LeakageRow): string => `${r.groupName ?? ''}|${r.key}`;

/**
 * The drawer's "edit inventory link" target. A leakage row carries no assignment id, so the
 * relink runs by (section, frozen key) — which needs a real, still-existing section to point
 * at; deleted ones return null and the drawer drops the action. Count/unit context is left
 * off on purpose: the row's quantities are in std units, not the count unit the modal shows.
 */
function relinkTargetFromLeakageRow(row: LeakageRow): RelinkTarget | null {
  // A pooled row stands for several products at once, so there is no single assignment to
  // re-point — and its `key` is synthetic, so relinking by it would target nothing. Drop the
  // action rather than guess a member; the user can open the member's own row instead.
  if (row.pool) return null;
  // Stock that only exists because a counted prep was exploded into it was never assigned
  // to any section, so the relink would target an assignment that isn't there. The product
  // to re-point is the prep — from its own section, on the Manage page.
  if (row.onlyFromSubrecipe) return null;
  const change = row.sectionChanges.find((c) => c.sectionKey === row.attributedSectionKey) ?? row.sectionChanges[0];
  if (!change || change.deleted) return null;
  return {
    inventorySectionProductId: null,
    productName: row.productName,
    sectionId: change.sectionKey,
    sectionName: change.sectionName,
    inventoryCategoryName: row.inventoryCategoryName,
    productKey: row.key,
  };
}

type DirFilter = 'favorites' | 'all' | 'negative' | 'positive' | 'highRisk';

/** Direction tab predicate for the product table. */
function matchesDir(r: LeakageRow, dir: DirFilter, favoriteKeys: string[]): boolean {
  if (dir === 'favorites') return isRowFavorite(r, favoriteKeys);
  if (dir === 'negative') return r.varianceValue !== null && r.varianceValue < 0;
  if (dir === 'positive') return r.varianceValue !== null && r.varianceValue > 0;
  if (dir === 'highRisk') return r.status === 'highRisk';
  return true;
}

/**
 * Sort key → comparable primitive for the product table.
 *
 * Quantity keys fall back to `varianceValue` on mixed-unit pools: their quantity fields are a
 * placeholder 0, so sorting on them would interleave those rows among real zeros. A no-op for
 * ordinary product rows and for pools whose members agree on a unit.
 */
function leakageSortValue(r: LeakageRow, key: string): string | number {
  const qtySort = (qty: number | null): number =>
    qty !== null && hasComparableQty(r) ? qty : r.varianceValue ?? Infinity;
  switch (key) {
    case 'productName':
      return r.productName.toLowerCase();
    case 'attributedSectionName':
      return (r.attributedSectionName ?? '').toLowerCase();
    case 'expectedQty':
      return qtySort(r.expectedQty);
    case 'endQty':
      return qtySort(r.endQty);
    case 'varianceQty':
      return qtySort(r.varianceQty);
    case 'varianceValue':
      return r.varianceValue ?? Infinity;
    case 'confidence':
      return r.confidence;
    case 'status':
      return r.status;
    case 'startQty':
      return qtySort(r.startQty);
    case 'purchasedQty':
      return qtySort(r.purchasedQty);
    case 'neededQty':
      return qtySort(r.neededQty);
    case 'inventoryCategoryName':
      return (r.inventoryCategoryName ?? '').toLowerCase();
    default:
      return 0;
  }
}

/** Merge per-group section rows (all-groups view) by section key; unattributed collapses to one row, last. */
function mergeSections(rows: VarianceSectionRow[]): VarianceSectionRow[] {
  const map = new Map<string, VarianceSectionRow>();
  rows.forEach((r) => {
    const ex = map.get(r.sectionKey);
    if (ex) {
      ex.expectedValue += r.expectedValue;
      ex.countedValue += r.countedValue;
      ex.varianceValue += r.varianceValue;
      ex.changeValue += r.changeValue;
    } else {
      map.set(r.sectionKey, { ...r });
    }
  });
  const merged = Array.from(map.values());
  merged.forEach((s) => {
    s.variancePct = s.expectedValue !== 0 ? s.varianceValue / s.expectedValue : null;
  });
  const rest = merged.filter((s) => s.sectionKey !== UNATTRIBUTED_KEY).sort((a, b) => a.varianceValue - b.varianceValue);
  const unattr = merged.filter((s) => s.sectionKey === UNATTRIBUTED_KEY);
  return [...rest, ...unattr];
}

/** Aggregate rows into category rows for the active mode (component-side, so single + all share it). */
function buildCatRows(
  rows: LeakageRow[],
  mode: CategoryMode,
  liveCategoryByKey: Map<string, string>,
  uncategorized: string
): LeakageCatRow[] {
  const map = new Map<string, { e: number; c: number; v: number; n: number }>();
  rows.forEach((r) => {
    const name =
      mode === 'inventory'
        ? r.inventoryCategoryName ?? uncategorized
        : liveCategoryByKey.get(anchorKeyOf(r)) ?? uncategorized;
    const cur = map.get(name) ?? { e: 0, c: 0, v: 0, n: 0 };
    cur.e += r.expectedValue ?? 0;
    cur.c += r.countedValue ?? 0;
    cur.v += r.varianceValue ?? 0;
    cur.n += 1;
    map.set(name, cur);
  });
  return Array.from(map.entries())
    .map(([name, x]) => ({
      name,
      expectedValue: x.e,
      countedValue: x.c,
      varianceValue: x.v,
      variancePct: x.e !== 0 ? x.v / x.e : null,
      productCount: x.n,
    }))
    .sort((a, b) => a.varianceValue - b.varianceValue);
}

/**
 * Turn leakage aggregates into ranked bars: biggest absolute leakage first, share of the
 * dimension's total |leakage| (so the bar width reads as "how much of the loss sits here"),
 * capped at TOP_BARS. The share denominator stays the FULL list, so the percentages keep
 * their meaning after the cap — and the muted rest bucket (unattributed) is appended last.
 */
function buildLeakageBars(
  entries: { id: string; label: string; value: number; changeValue?: number | null; productCount: number; muted?: boolean }[],
  subline: (productCount: number) => ReactNode,
  onSelect: () => void
): RankedBarRow[] {
  const denom = entries.reduce((sum, e) => sum + Math.abs(e.value), 0);
  const toBar = (e: (typeof entries)[number]): RankedBarRow => ({
    id: e.id,
    label: e.label,
    value: e.value,
    share: denom > 0 ? Math.abs(e.value) / denom : 0,
    changeValue: e.changeValue ?? null,
    subline: subline(e.productCount),
    muted: e.muted,
    onClick: onSelect,
  });
  const ranked = entries.filter((e) => !e.muted).sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
  const rest = entries.filter((e) => e.muted);
  return [...ranked.slice(0, TOP_BARS).map(toBar), ...rest.map(toBar)];
}

/**
 * Leakage tab of the inventory page (INVCOUNT p6). Renders — lazily, only when the
 * tab is active — the whole variance analysis from `computeInventoryVariance` /
 * `computeInventoryVarianceAll`, top to bottom: banners, KPI row,
 * the product table as the page's centerpiece, the
 * two breakdown charts (category + section, each with a full-table "see all"), the
 * custom-ingredients strip, and the per-product detail drawer (stock movement,
 * per-section changes, sales usage, and a static possible-causes checklist).
 */
const LeakageTab = ({ restaurantId, selection, canExport, restaurantName, onBaselineChange }: LeakageTabProps) => {
  const { t } = useTranslation();
  const practiceInventory = useInventoryPracticeDataset();
  const practice = usePracticeDataset(!!practiceInventory);
  const practiceActive = !!practiceInventory;
  const [practiceUnitMode, setPracticeUnitMode] = useState<LeakageUnitMode>('count');
  const [practiceFavorites, setPracticeFavorites] = useState<string[]>([]);
  const [productPage, setProductPage] = useState(1);

  // Lazy data loading — only fires because this component is mounted (tab active).
  useLoadOrderHistory();
  useLoadRevenueHistory();
  useLoadCostPrice();
  useLoadSubrecipes();

  const realOrders = useOrderHistory((s) => s.orderHistoryList);
  const ordersLoading = useOrderHistory((s) => s.isLoading);
  const realRevenue = useRevenueHistory((s) => s.revenueHistoryList);
  const revenueLoading = useRevenueHistory((s) => s.isLoading);
  const realCostPriceList = useCostPriceStore((s) => s.costPriceList);
  const costLoading = useCostPriceStore((s) => s.isLoading);
  const realSubrecipesById = useSubrecipesStore((s) => s.subrecipesById);
  const realCountsHistory = useInventoryStore((s) => s.countsHistory);
  const isLoadingCounts = useInventoryStore((s) => s.isLoadingCounts);
  const loadCountsHistory = useInventoryStore((s) => s.loadCountsHistory);
  const realCanonicalUnitsByGroup = useInventoryStore((s) => s.canonicalUnitsByGroup);
  const loadCanonicalUnits = useInventoryStore((s) => s.loadCanonicalUnits);
  // Std units or the unit staff count in — remembered per user, like the ex-VAT toggles.
  const realUnitMode = useAppUserDefaultStore((s) => s.leakageQtyUnit);
  const orders = practice?.orderHistoryList ?? realOrders;
  const revenue = practice?.revenueHistoryList ?? realRevenue;
  const costPriceList = practice?.costPriceList ?? realCostPriceList;
  const subrecipesById = practice?.subrecipesById ?? realSubrecipesById;
  const countsHistory = practiceInventory?.countsHistory ?? realCountsHistory;
  const canonicalUnitsByGroup = practiceInventory?.canonicalUnitsByGroup ?? realCanonicalUnitsByGroup;
  const unitMode = practiceActive ? practiceUnitMode : realUnitMode;
  const qtyFormat = useMemo(() => makeLeakageQtyFormat(unitMode), [unitMode]);
  // The startup batch normally loads this alongside the ex-VAT defaults; this is the same
  // safety net `OrderVatToggle` gives the orders toggle, for the case where the tab renders
  // before (or without) that batch — it self-skips once loaded, so it costs one no-op call.
  useEffect(() => {
    void loadLeakageQtyUnitSetting();
  }, []);
  const reportSettings = useRestaurantStore((s) => (restaurantId ? s.reportSettings[restaurantId] : undefined));
  const setReportSettings = useRestaurantStore((s) => s.setReportSettings);
  const defaultWastePercentage = useMemo(
    () => practiceInventory && practiceInventory.source !== 'customer' ? 0 : resolveWastePercentage(reportSettings?.wastePercentage),
    [reportSettings, practiceInventory]
  );

  // Starred inventory keys (restaurant-wide) driving the product table's Favorites tab,
  // mirroring the utilization report: the store is the source of truth, toggling writes
  // it optimistically, then persists and reverts on failure.
  const realFavoriteKeys = useMemo(() => reportSettings?.inventoryFavoriteProducts ?? [], [reportSettings]);
  const favoriteKeys = practiceActive ? practiceFavorites : realFavoriteKeys;

  // Starring a pool writes its PRIMARY member's key; unstarring clears every member's, so a
  // pool starred through an alternative can still be turned off in one click.
  const handleToggleFavorite = useCallback(
    (row: LeakageRow) => {
      const next = toggleFavoriteKeys(row, favoriteKeys);
      if (practiceActive || getInventoryPracticeDataset() || isInventoryPracticeReference(row.sectionChanges)) {
        setPracticeFavorites(next);
        return;
      }
      if (!restaurantId || !reportSettings) return;
      setReportSettings(restaurantId, { ...reportSettings, inventoryFavoriteProducts: next });
      updateRestaurantFavoriteProducts(restaurantId, next, 'inventory').catch((err) => {
        console.error('Failed to save inventory favorite products', err);
        setReportSettings(restaurantId, { ...reportSettings, inventoryFavoriteProducts: favoriteKeys });
      });
    },
    [restaurantId, reportSettings, favoriteKeys, setReportSettings, practiceActive]
  );

  const isAll = selection.groupId === null;
  const groupKey = selection.groupId ?? '';

  // Resolve the "viewing" (end) session key from the page selection: an explicit
  // session id, else the group's newest session (Latest).
  const sessions = useMemo(() => groupCountsIntoSessions(countsHistory), [countsHistory]);
  const endSessionKey = useMemo(() => {
    if (isAll) return '';
    if (selection.endSessionKey) return selection.endSessionKey;
    if (selection.sessionId) return `session:${selection.sessionId}`;
    const newest = sessions.find((s) => (s.groupId ?? s.groupName ?? 'ungrouped') === groupKey);
    return newest?.key ?? '';
  }, [isAll, selection.sessionId, selection.endSessionKey, sessions, groupKey]);

  // Real inventory-group ids this view reconciles. `groupKeyOf` uses the groupId whenever a
  // session has one, so a legacy/ungrouped bucket simply has no assignments to fetch.
  const unitGroupIds = useMemo(() => {
    if (!isAll) return selection.groupId ? [selection.groupId] : [];
    const sessionCount = new Map<string, number>();
    sessions.forEach((s) => {
      const k = s.groupId ?? s.groupName ?? 'ungrouped';
      sessionCount.set(k, (sessionCount.get(k) ?? 0) + 1);
    });
    // Only groups the all-groups view actually reconciles — the same ≥2-sessions rule
    // `computeInventoryVarianceAll` applies.
    return Array.from(new Set(sessions.map((s) => s.groupId).filter((id): id is string => !!id))).filter(
      (id) => (sessionCount.get(id) ?? 0) >= 2
    );
  }, [isAll, selection.groupId, sessions]);

  // Lazy: the canonical units are fetched only once someone switches the view on, so a user
  // who never flips the toggle pays nothing. While a group is still loading its rows simply
  // fall back to std units by construction — no spinner, no layout shift.
  useEffect(() => {
    if (practiceActive || unitMode !== 'count' || !restaurantId) return;
    unitGroupIds.forEach((gid) => void loadCanonicalUnits(gid, restaurantId));
  }, [unitMode, restaurantId, unitGroupIds, loadCanonicalUnits, practiceActive]);

  // `undefined` in std mode, so the engine's memo key (and its output) is untouched until the
  // view is opted into. Keyed by groupKey, which IS the groupId for every real group.
  const canonicalCountUnits = useMemo(() => {
    if (unitMode !== 'count') return undefined;
    const byGroup = new Map<string, Map<string, CanonicalCountUnit>>();
    unitGroupIds.forEach((gid) => {
      const loaded = canonicalUnitsByGroup[gid];
      if (loaded) byGroup.set(gid, loaded);
    });
    return byGroup.size > 0 ? byGroup : undefined;
  }, [unitMode, unitGroupIds, canonicalUnitsByGroup]);

  // Baseline ("Compared against") options: sessions strictly older than the end.
  const baselineOptions = useMemo(
    () => (isAll || !endSessionKey ? [] : buildVarianceBaselineOptions(countsHistory, groupKey, endSessionKey)),
    [isAll, countsHistory, groupKey, endSessionKey]
  );

  // Tab-local baseline choice, reset to the previous session whenever the page
  // selection (group / end session) changes.
  const [localBaselineKey, setBaselineKey] = useState('');
  const baselineKey = selection.baselineSessionKey ?? localBaselineKey;
  const restoredBaseline = useRef<string | null>(null);
  useEffect(() => {
    setBaselineKey(restoredBaseline.current ?? baselineOptions[0]?.key ?? '');
    restoredBaseline.current = null;
  }, [baselineOptions]);

  // Card / table UI state.
  const [categoryMode, setCategoryMode] = useState<CategoryMode>('inventory');
  const [searchQuery, setSearchQuery] = useState('');
  // Favorites is the landing tab once anything is starred, else All. Derived rather than
  // seeded into state because favourites arrive with the async restaurant load, and it
  // falls back to All the moment the last favourite is unstarred.
  const [pickedDir, setPickedDir] = useState<DirFilter | null>(null);
  const hasFavorites = favoriteKeys.length > 0;
  const picked = pickedDir === 'favorites' && !hasFavorites ? null : pickedDir;
  const dirFilter: DirFilter = picked ?? (hasFavorites ? 'favorites' : 'all');
  const [confFilter, setConfFilter] = useState<'all' | VarianceConfidence>('all');
  const [catFilter, setCatFilter] = useState('all');
  const [productCatFilter, setProductCatFilter] = useState('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersRef = useRef<HTMLDivElement>(null);
  const [mobileLimit, setMobileLimit] = useState(20);
  useEffect(() => {
    if (!filtersOpen) return;
    const closeOutside = (event: MouseEvent) => {
      if (!filtersRef.current?.contains(event.target as Node)) setFiltersOpen(false);
    };
    const closeEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') setFiltersOpen(false); };
    document.addEventListener('mousedown', closeOutside);
    document.addEventListener('keydown', closeEscape);
    return () => {
      document.removeEventListener('mousedown', closeOutside);
      document.removeEventListener('keydown', closeEscape);
    };
  }, [filtersOpen]);
  useEffect(() => {
    setMobileLimit(20);
  }, [searchQuery, confFilter, catFilter, productCatFilter, dirFilter, endSessionKey, baselineKey]);
  const [sortColumn, setSortColumn] = useState<string | null>('varianceValue');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>('asc');
  // Pool rows whose members are showing, by pool row key.
  const [expandedPools, setExpandedPools] = useState<Set<string>>(new Set());
  // Breakdown charts show the top bars; the full per-dimension table lives in a slide-over.
  const [sectionsSlideOpen, setSectionsSlideOpen] = useState(false);
  const [catSlideOpen, setCatSlideOpen] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  // The drawer holds an id, not a row: the row object is re-derived every render so an edit
  // made from inside the drawer (cost price, relink) shows up without reopening it.
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  // Modals the drawer's actions open — owned here, so they outlive the drawer's own chrome.
  const [editCostPriceId, setEditCostPriceId] = useState<string | null>(null);
  const [setupRow, setSetupRow] = useState<LeakageRow | null>(null);
  const [relinkTarget, setRelinkTarget] = useState<RelinkTarget | null>(null);

  const viewSnapshot = useRef<{
    baselineKey: string; categoryMode: CategoryMode; searchQuery: string;
    pickedDir: DirFilter | null; confFilter: typeof confFilter; catFilter: string; productCatFilter: string;
    sortColumn: string | null; sortDirection: typeof sortDirection; expandedPools: Set<string>;
  } | null>(null);
  useLayoutEffect(() => {
    if (practiceActive && !viewSnapshot.current) {
      viewSnapshot.current = { baselineKey, categoryMode, searchQuery, pickedDir, confFilter, catFilter, productCatFilter, sortColumn, sortDirection, expandedPools };
      setCategoryMode('inventory');
      setSearchQuery('');
      setPickedDir('all');
      setConfFilter('all');
      setCatFilter('all');
      setProductCatFilter('all');
      setFiltersOpen(false);
      setSortColumn('varianceValue');
      setSortDirection('asc');
      setExpandedPools(new Set());
      setPracticeUnitMode('count');
      setPracticeFavorites(isCustomerInventoryPractice() ? [...realFavoriteKeys] : []);
    } else if (!practiceActive && viewSnapshot.current) {
      const previous = viewSnapshot.current;
      viewSnapshot.current = null;
      restoredBaseline.current = previous.baselineKey;
      setBaselineKey(previous.baselineKey);
      setCategoryMode(previous.categoryMode);
      setSearchQuery(previous.searchQuery);
      setPickedDir(previous.pickedDir);
      setConfFilter(previous.confFilter);
      setCatFilter(previous.catFilter);
      setProductCatFilter(previous.productCatFilter);
      setSortColumn(previous.sortColumn);
      setSortDirection(previous.sortDirection);
      setExpandedPools(previous.expandedPools);
      setSelectedUid(null);
      setSetupRow(null);
      setEditCostPriceId(null);
      setRelinkTarget(null);
      setCatSlideOpen(false);
      setSectionsSlideOpen(false);
    }
    // Snapshot once per practice session; changing a local control must not recapture it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceActive]);

  // Product-category label + supplier per inventory key, from the latest order carrying the
  // product — mirrors ValueByCategoryCard's live categories via categoryUtils. One pass over
  // orders feeds both the category breakdown (product mode) and the drawer's supplier chip.
  const liveByKey = useMemo(() => {
    const category = new Map<string, string>();
    const supplier = new Map<string, { platform: string; name?: string }>();
    const identity = new Map<string, LinkedOrderProduct>();
    const sorted = [...orders].sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());
    for (const o of sorted) {
      for (const p of o.productData) {
        const key = inventoryKey(o.platform, p.articleId, p.name);
        if (!key || category.has(key)) continue;
        category.set(key, getCategoryDisplayName(p.category, p.mainCategory) ?? t('inventory.uncategorizedBucket'));
        supplier.set(key, {
          platform: o.platform,
          name: (o.unknownSupplierName ?? '').trim() || formatPlatformName(o.platform),
        });
        // Orders are walked newest-first and each key is taken once, so this IS the last
        // order carrying the product — the identity the inventory row is linked to.
        identity.set(key, {
          productName: p.name,
          platform: o.platform,
          unknownSupplierName: (o.unknownSupplierName ?? '').trim() || undefined,
          articleId: p.articleId,
          // Price is NOT taken from the order line — see LinkedOrderProduct.
          pricePerStdUnit: null,
          stdUnit: '',
          // orderDate is typed Date but arrives as a JSON string at runtime — always coerce.
          lastOrderDate: (() => {
            const d = new Date(o.orderDate);
            return isNaN(d.getTime()) ? null : d;
          })(),
        });
      }
    }
    return { category, supplier, identity };
  }, [orders, t]);
  const liveCategoryByKey = liveByKey.category;

  const report = useMemo(() => {
    if (isAll || !endSessionKey || !baselineKey) return null;
    return cachedReportValue(
      'leakage.data',
      [countsHistory, orders, revenue, costPriceList, subrecipesById, defaultWastePercentage, restaurantId, groupKey, endSessionKey, baselineKey, canonicalCountUnits],
      () =>
        computeInventoryVariance({
          countsHistory,
          orders,
          revenue,
          costPriceList,
          subrecipesById,
          defaultWastePercentage,
          currentRestaurantId: restaurantId,
          selection: { groupKey, endSessionKey, baselineSessionKey: baselineKey },
          canonicalCountUnits,
        })
    );
  }, [isAll, countsHistory, orders, revenue, costPriceList, subrecipesById, defaultWastePercentage, restaurantId, groupKey, endSessionKey, baselineKey, canonicalCountUnits]);

  const allReport = useMemo(() => {
    if (!isAll) return null;
    return cachedReportValue(
      'leakage.all',
      [countsHistory, orders, revenue, costPriceList, subrecipesById, defaultWastePercentage, restaurantId, canonicalCountUnits],
      () =>
        computeInventoryVarianceAll({
          countsHistory,
          orders,
          revenue,
          costPriceList,
          subrecipesById,
          defaultWastePercentage,
          currentRestaurantId: restaurantId,
          canonicalCountUnits,
        })
    );
  }, [isAll, countsHistory, orders, revenue, costPriceList, subrecipesById, defaultWastePercentage, restaurantId, canonicalCountUnits]);

  // Close the product drawer whenever the selection (group / end / baseline) changes.
  useEffect(() => {
    setSelectedUid(null);
  }, [isAll, groupKey, endSessionKey, baselineKey]);

  const rowsRef = useRef<LeakageRow[]>([]);
  rowsRef.current = ((isAll ? allReport?.combined.rows : report?.rows) ?? []) as LeakageRow[];

  useLayoutEffect(() => {
    const totals = isAll
      ? allReport && allReport.perGroup.length > 0
        ? allReport.combined
        : null
      : report && report.hasData
        ? report
        : null;
    if (!totals) {
      publishLeakageTourState(null);
      return;
    }
    const windowInfo = isAll ? allReport?.perGroup[0]?.data.window : report?.window;
    publishLeakageTourState(
      buildLeakageTourSnapshot({
        expected: totals.expectedTotalValue,
        counted: totals.countedTotalValue,
        leakage: totals.varianceTotalValue,
        confidenceScore: totals.confidenceScore,
        linked: totals.linkedProductCount,
        total: totals.totalProductCount,
        startLabel: windowInfo?.startLabel ?? '',
        endLabel: windowInfo?.endLabel ?? '',
        rows: rowsRef.current,
      })
    );
  }, [isAll, report, allReport]);

  useEffect(() => () => publishLeakageTourState(null), []);

  useEffect(() => {
    const onUnit = (e: Event) => {
      if (!getInventoryPracticeDataset()) return;
      const unit = (e as CustomEvent<{ unit?: LeakageUnitMode }>).detail?.unit;
      if (unit === 'count' || unit === 'std') setPracticeUnitMode(unit);
    };
    const onPracticeReset = () => {
      if (!getInventoryPracticeDataset()) return;
      setPracticeUnitMode('count');
      setPracticeFavorites(isCustomerInventoryPractice() ? [...realFavoriteKeys] : []);
      setCategoryMode('inventory');
      setSortColumn('varianceValue');
      setSortDirection('asc');
      setExpandedPools(new Set());
    };
    const onDir = (e: Event) => {
      if (!getInventoryPracticeDataset()) return;
      const dir = (e as CustomEvent<{ dir?: DirFilter }>).detail?.dir;
      if (dir === 'all' || dir === 'negative' || dir === 'positive' || dir === 'highRisk' || dir === 'favorites') {
        setPickedDir(dir);
      }
    };
    const onBaseline = (e: Event) => {
      if (!getInventoryPracticeDataset()) return;
      const key = (e as CustomEvent<{ key?: string }>).detail?.key;
      if (key) {
        setBaselineKey(key);
        onBaselineChange?.(key);
      }
    };
    const onReset = () => {
      if (!getInventoryPracticeDataset()) return;
      setPickedDir('all');
      setSearchQuery('');
      setConfFilter('all');
      setCatFilter('all');
      setProductCatFilter('all');
      setFiltersOpen(false);
      setSelectedUid(null);
      setSetupRow(null);
    };
    const onOpenRow = (e: Event) => {
      if (!getInventoryPracticeDataset()) return;
      const uid = (e as CustomEvent<{ uid?: string }>).detail?.uid || leakageWalkthroughRowUid();
      const rows = rowsRef.current.filter(row => isCustomerInventoryPractice() || isInventoryPracticeReference(row.sectionChanges));
      const match = uid ? rows.find((r) => rowUid(r) === uid) : null;
      const top = match ?? [...rows].sort((a, b) => (a.varianceValue ?? Infinity) - (b.varianceValue ?? Infinity))[0];
      if (!top) return;
      const next = rowUid(top);
      if (selectedUid === next) return;
      setSelectedUid(next);
    };
    const onAddLink = () => {
      if (!getInventoryPracticeDataset()) return;
      const rows = rowsRef.current.filter(row => isCustomerInventoryPractice() || isInventoryPracticeReference(row.sectionChanges));
      const uid = selectedUid ?? leakageWalkthroughRowUid();
      const match = uid ? rows.find((r) => rowUid(r) === uid) : null;
      const top = match ?? [...rows].sort((a, b) => (a.varianceValue ?? Infinity) - (b.varianceValue ?? Infinity))[0];
      if (!top) return;
      const next = rowUid(top);
      setSelectedUid(next);
      setSetupRow((prev) => (prev && rowUid(prev) === next ? prev : top));
    };
    const onCloseSetup = () => { if (getInventoryPracticeDataset()) setSetupRow(null); };
    const onCloseAll = () => {
      if (!getInventoryPracticeDataset()) return;
      setSelectedUid(null);
      setSetupRow(null);
      setEditCostPriceId(null);
      setRelinkTarget(null);
      setCatSlideOpen(false);
      setSectionsSlideOpen(false);
    };
    const onOpenEdit = () => {
      if (!getInventoryPracticeDataset()) return;
      const dish = leakageSetupDish().trim().toLowerCase();
      const analysis = costPriceList.find(
        (a) => (a.menuItemName || a.costPriceName || '').trim().toLowerCase() === dish
      );
      if (analysis?.id && isPracticeId(analysis.id)) setEditCostPriceId(analysis.id);
    };
    const onCatMode = (e: Event) => {
      if (!getInventoryPracticeDataset()) return;
      const mode = (e as CustomEvent<{ mode?: CategoryMode }>).detail?.mode;
      if (mode === 'inventory' || mode === 'product') setCategoryMode(mode);
    };
    window.addEventListener('onboarding:inventory-leakage-unit', onUnit);
    window.addEventListener('onboarding:inventory-reset-view', onPracticeReset);
    window.addEventListener('onboarding:inventory-leakage-dir', onDir);
    window.addEventListener('onboarding:inventory-leakage-baseline', onBaseline);
    window.addEventListener('onboarding:inventory-leakage-reset', onReset);
    window.addEventListener('onboarding:inventory-leakage-open-row', onOpenRow);
    window.addEventListener('onboarding:inventory-leakage-add-link', onAddLink);
    window.addEventListener('onboarding:inventory-leakage-close-setup', onCloseSetup);
    window.addEventListener('onboarding:inventory-leakage-close-all', onCloseAll);
    window.addEventListener('onboarding:inventory-leakage-open-edit', onOpenEdit);
    window.addEventListener('onboarding:inventory-category-mode', onCatMode);
    return () => {
      window.removeEventListener('onboarding:inventory-leakage-unit', onUnit);
      window.removeEventListener('onboarding:inventory-reset-view', onPracticeReset);
      window.removeEventListener('onboarding:inventory-leakage-dir', onDir);
      window.removeEventListener('onboarding:inventory-leakage-baseline', onBaseline);
      window.removeEventListener('onboarding:inventory-leakage-reset', onReset);
      window.removeEventListener('onboarding:inventory-leakage-open-row', onOpenRow);
      window.removeEventListener('onboarding:inventory-leakage-add-link', onAddLink);
      window.removeEventListener('onboarding:inventory-leakage-close-setup', onCloseSetup);
      window.removeEventListener('onboarding:inventory-leakage-close-all', onCloseAll);
      window.removeEventListener('onboarding:inventory-leakage-open-edit', onOpenEdit);
      window.removeEventListener('onboarding:inventory-category-mode', onCatMode);
    };
  }, [selectedUid, costPriceList, onBaselineChange, realFavoriteKeys]);

  // Period-independent per-unit needs, reused to resolve a clicked product's
  // sales-usage breakdown (the drawer's "used in which dishes" table).
  // Keyed by inventoryKey so the usage map lines up with the variance rows' inventory keys.
  const perUnit = useMemo(
    () => buildPerUnitNeeds(costPriceList, subrecipesById, defaultWastePercentage, restaurantId, inventoryKey),
    [costPriceList, subrecipesById, defaultWastePercentage, restaurantId]
  );

  // Per-dish sales usage for the selected product, scoped to its group's window. Resolved from
  // the report rows (not the rendered view) so it stays above the loading/empty early returns.
  const selectedUsage = useMemo<LeakageDishUsage[]>(() => {
    if (!selectedUid) return [];
    const rows = (isAll ? allReport?.combined.rows : report?.rows) as LeakageRow[] | undefined;
    const row = rows?.find((r) => rowUid(r) === selectedUid);
    if (!row) return [];
    let win: { startAt: number; endAt: number } | null = null;
    // Rows used for POOL SCALING must come from the same group as the revenue window: a pool
    // key repeats across groups and `buildPerDishUsage` scales a key once, off the first row
    // carrying it — hand it the combined list and another group's total can scale this drawer.
    // (Finding the row above still needs the combined list; only the scaling input is scoped.)
    let scalingRows: LeakageRow[] | undefined;
    if (isAll && allReport) {
      const g = allReport.perGroup.find((pg) => (pg.groupName ?? null) === (row.groupName ?? null));
      win = g?.data.window ? { startAt: g.data.window.startAt, endAt: g.data.window.endAt } : null;
      scalingRows = g?.data.rows as LeakageRow[] | undefined;
    } else if (report?.window) {
      win = { startAt: report.window.startAt, endAt: report.window.endAt };
      scalingRows = report.rows as LeakageRow[];
    }
    if (!win) return [];
    const bounds = win;
    const windowRevenue = revenue.filter((r) => {
      const tms = new Date(r.date).getTime();
      return !Number.isNaN(tms) && tms >= bounds.startAt && tms <= bounds.endAt;
    });
    // Pool-aware: pooled demand is emitted once under the pool key and scaled to the row's usage.
    const usage = buildPerDishUsage(perUnit, windowRevenue, scalingRows).get(row.key) ?? [];
    // Each line gets the cost-price analysis a user would edit to change it, so the drawer
    // can offer "edit dish" (see the util for the overridden-choice rule it encodes).
    const sold = attachUsageCostPriceIds(usage, costPriceList);

    // One breakdown list answering "what was used" AND "where is this set up" (see the util).
    return mergeSetupsIntoUsage(sold, {
      productKey: anchorKeyOf(row),
      costPriceList,
      subrecipesById,
      perUnit,
      basePerUnitDivisor: row.neededUnitFactor,
    });
  }, [selectedUid, isAll, allReport, report, revenue, perUnit, costPriceList, subrecipesById]);

  // While the underlying data is still streaming in for the first time, show a
  // spinner instead of flashing the "need two counts" empty state.
  const loadingInputs = !practiceActive && (
    (isLoadingCounts && countsHistory.length === 0) ||
    (ordersLoading && orders.length === 0) ||
    (revenueLoading && revenue.length === 0) ||
    (costLoading && costPriceList.length === 0));
  if (loadingInputs) {
    return (
      <div className="flex justify-center py-16">
        <LoaderCircle className={cn('h-8 w-8 animate-spin', primaryText)} />
      </div>
    );
  }

  const totals: LeakageTotals | null = isAll
    ? allReport && allReport.perGroup.length > 0
      ? allReport.combined
      : null
    : report && report.hasData
    ? report
    : null;

  // Empty state: fewer than two comparable counts (single group) or no group with ≥2 (All).
  if (!totals) {
    return (
      <div className="flex flex-col items-center">
        <NoDataInPeriod />
        <p className={cn(smallTextSize, tertiaryText, 'max-w-md text-center -mt-4 mb-6')}>
          {t('inventory.leakage.needTwoCounts')}
        </p>
      </div>
    );
  }

  // Normalised view for the breakdown cards / product table.
  const view: LeakageView =
    isAll && allReport
      ? {
          isAll: true,
          rows: allReport.combined.rows as LeakageRow[],
          sections: mergeSections(allReport.combined.sections),
          customItems: [],
          excluded: [],
          unmeasurableProductNames: [],
          unattributedVarianceValue: allReport.combined.unattributedVarianceValue,
          varianceTotalValue: allReport.combined.varianceTotalValue,
          unattributedShare: allReport.combined.unattributedShare,
        }
      : {
          isAll: false,
          rows: (report?.rows ?? []) as LeakageRow[],
          sections: report?.sections ?? [],
          customItems: report?.customItems ?? [],
          excluded: report?.excluded ?? [],
          unmeasurableProductNames: report?.unmeasurableProductNames ?? [],
          unattributedVarianceValue: report?.unattributedVarianceValue ?? 0,
          varianceTotalValue: report?.varianceTotalValue ?? 0,
          unattributedShare: report?.unattributedShare ?? 0,
        };

  const countedSub =
    !isAll && report?.window ? t('inventory.leakage.kpiCountedSub', { date: report.window.endLabel }) : undefined;

  // Table categories are independent from one another and from the chart toggle.
  const catLabelOf = (r: LeakageRow): string => r.inventoryCategoryName ?? t('inventory.uncategorizedBucket');
  const productCatLabelOf = (r: LeakageRow): string => liveCategoryByKey.get(anchorKeyOf(r)) ?? t('inventory.uncategorizedBucket');
  const categoryFilterOptions = Array.from(new Set(view.rows.map(catLabelOf))).sort((a, b) => a.localeCompare(b));
  const productCategoryFilterOptions = Array.from(new Set(view.rows.map(productCatLabelOf))).sort((a, b) => a.localeCompare(b));
  const activeFilterCount = [confFilter, catFilter, productCatFilter].filter((value) => value !== 'all').length;
  const clearTableFilters = () => {
    setConfFilter('all');
    setCatFilter('all');
    setProductCatFilter('all');
  };

  // Search + confidence + category first; direction applies on top so the
  // direction tab counts reflect the other active filters.
  const q = searchQuery.trim().toLowerCase();
  const baseRows = view.rows.filter((r) => {
    if (q && !rowMatchesSearch(r, q)) return false;
    if (confFilter !== 'all' && r.confidence !== confFilter) return false;
    if (catFilter !== 'all' && catLabelOf(r) !== catFilter) return false;
    if (productCatFilter !== 'all' && productCatLabelOf(r) !== productCatFilter) return false;
    return true;
  });
  const filteredRows = baseRows.filter((r) => matchesDir(r, dirFilter, favoriteKeys));
  const sortedRows =
    sortColumn && sortDirection
      ? [...filteredRows].sort((a, b) => {
          const av = leakageSortValue(a, sortColumn);
          const bv = leakageSortValue(b, sortColumn);
          if (av < bv) return sortDirection === 'asc' ? -1 : 1;
          if (av > bv) return sortDirection === 'asc' ? 1 : -1;
          return 0;
        })
      : filteredRows;

  // Sorting and pagination operate on reconciled rows; an expanded pool's members are injected
  // straight after their own row, so they travel with it and never sort away from it.
  const displayRows: LeakageDisplayRow[] = sortedRows.flatMap((row) =>
    row.members && expandedPools.has(row.key)
      ? [
          { kind: 'row' as const, row },
          ...row.members.map((member) => ({ kind: 'member' as const, member, parent: row })),
        ]
      : [{ kind: 'row' as const, row }]
  );

  // Match Count Review's 2-state toggle (asc ↔ desc); first click on a new column
  // sorts worst/highest-first, the name column starts A→Z.
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection(column === 'productName' ? 'asc' : 'desc');
    }
  };

  const catRows = buildCatRows(view.rows, categoryMode, liveCategoryByKey, t('inventory.uncategorizedBucket'));

  // Drawer subject: re-derived from the current rows every render, so a cost-price or relink
  // edit made from inside the drawer lands on fresh numbers instead of the row we opened with.
  const selectedRow = selectedUid ? view.rows.find((r) => rowUid(r) === selectedUid) ?? null : null;
  const selectedWindow = selectedRow
    ? isAll
      ? allReport?.perGroup.find((pg) => (pg.groupName ?? null) === (selectedRow.groupName ?? null))?.data.window ?? null
      : report?.window ?? null
    : null;
  const selectedRelink = selectedRow ? relinkTargetFromLeakageRow(selectedRow) : null;
  // The order product this row is linked to, keyed on the row's real product identity — a
  // pool answers through its primary member.
  const selectedIdentity = selectedRow ? liveByKey.identity.get(anchorKeyOf(selectedRow)) ?? null : null;
  // The valuation price the leakage math actually used, from the row itself (a pool answers
  // through its primary member, whose own price chain priced its share).
  const selectedPrimary = selectedRow?.members?.find((m) => m.isPrimary);
  const selectedLinked: LinkedOrderProduct | null =
    selectedIdentity && selectedRow
      ? {
          ...selectedIdentity,
          pricePerStdUnit: selectedPrimary ? selectedPrimary.pricePerStdUnit : selectedRow.pricePerStdUnit,
          stdUnit: selectedPrimary ? selectedPrimary.stdUnit : selectedRow.stdUnit,
        }
      : null;

  const drawerSupplier = (r: LeakageRow): { platform: string; name?: string } | null => {
    const primary = r.members?.find((m) => m.isPrimary);
    if (primary) return { platform: primary.platform, name: primary.unknownSupplierName ?? formatPlatformName(primary.platform) };
    return liveByKey.supplier.get(anchorKeyOf(r)) ?? null;
  };

  /** The report a row belongs to — its own group's in the all-groups view, else the only one. */
  const reportOfGroup = (groupName: string | null | undefined) =>
    isAll
      ? allReport?.perGroup.find((pg) => (pg.groupName ?? null) === (groupName ?? null))?.data ?? null
      : report ?? null;

  // The product table exactly as scoped on screen — the rows the table is showing (direction
  // tab, favourites, search, confidence, category) in the sort it is showing, for the selected
  // inventory / count / "compared against" baseline. Each row also carries the facts its drawer
  // adds. Built on click, so it costs nothing until asked for.
  const buildExportEntries = (): LeakageCsvEntry[] =>
    sortedRows.map((row) => {
      // Keyed on the row's real product identity — a pool answers through its primary member.
      const identity = liveByKey.identity.get(anchorKeyOf(row)) ?? null;
      const w = reportOfGroup(row.groupName)?.window ?? null;
      return {
        row,
        supplierName: drawerSupplier(row)?.name ?? null,
        articleId: identity?.articleId ?? row.members?.find((m) => m.isPrimary)?.articleId ?? null,
        lastOrderDate: identity?.lastOrderDate ?? null,
        periodLabel: w ? `${w.startLabel} → ${w.endLabel}` : '',
      };
    });

  const handleExportCsv = () => {
    downloadCsv(
      buildLeakageCsv(t, buildExportEntries(), isAll, qtyFormat),
      leakageCsvFilename(restaurantName, new Date())
    );
  };

  // Same rows, plus the lines the drawer explains each one with: pool members, per-section
  // movement, and the per-dish usage behind the theoretical-usage figure. All of it is derived
  // on click — the usage walk is per GROUP WINDOW (not per row), so the file costs one pass.
  const handleExportDetailedCsv = () => {
    const setupsByKey = buildProductSetupsIndex(costPriceList, subrecipesById);
    const usageByGroup = new Map<string, Map<string, UsageWithCostPrice[]>>();

    const usageOfGroup = (groupName: string | null): Map<string, UsageWithCostPrice[]> => {
      const cacheKey = groupName ?? '';
      const cached = usageByGroup.get(cacheKey);
      if (cached) return cached;

      const data = reportOfGroup(groupName);
      const win = data?.window ?? null;
      const map = new Map<string, UsageWithCostPrice[]>();
      if (data && win) {
        const windowRevenue = revenue.filter((r) => {
          const tms = new Date(r.date).getTime();
          return !Number.isNaN(tms) && tms >= win.startAt && tms <= win.endAt;
        });
        // Pool-aware, and scoped to THIS group's rows: a pool key repeats across groups and is
        // scaled once, off the first row carrying it (see `buildPerDishUsage`).
        const perKey = Array.from(buildPerDishUsage(perUnit, windowRevenue, data.rows).entries());
        // One resolve pass for the whole group: `attachUsageCostPriceIds` rebuilds its dish
        // index per call, so calling it per product would rescan every cost price per row.
        const attached = attachUsageCostPriceIds(
          perKey.flatMap(([, lines]) => lines),
          costPriceList
        );
        let at = 0;
        for (const [key, lines] of perKey) {
          map.set(key, attached.slice(at, at + lines.length));
          at += lines.length;
        }
      }
      usageByGroup.set(cacheKey, map);
      return map;
    };

    const entries: LeakageDetailedCsvEntry[] = buildExportEntries().map((entry) => {
      const { row } = entry;
      // Setup facts hang off the real product key, while the usage lines hang off the row key
      // (a pool's is synthetic) — the drawer's split, kept here.
      const setupById = new Map((setupsByKey.get(anchorKeyOf(row)) ?? []).map((s) => [s.costPriceId, s]));
      const dishes: UsageWithSetup[] = (usageOfGroup(row.groupName ?? null).get(row.key) ?? []).map((u) => {
        const setup = u.costPriceId ? setupById.get(u.costPriceId) : undefined;
        return { ...u, viaAlternative: setup?.viaAlternative, viaSubrecipe: setup?.viaSubrecipe };
      });
      return { ...entry, dishes };
    });

    downloadCsv(
      buildLeakageDetailedCsv(t, entries, isAll, qtyFormat),
      leakageCsvFilename(restaurantName, new Date(), 'detailed')
    );
  };

  // --- product table columns ------------------------------------------------
  // Pool rows and their member rows share ONE column set so member facts line up under the
  // aggregates they belong to (the utilization grouped table's rule). Members carry FACTS
  // only — expected and leakage stay on the pool row, because a member's share of them is
  // an allocation estimate, not an observation.
  const memberDash = <span className={tertiaryText}>—</span>;

  const renderLeakageQuantity = (row: LeakageRow): ReactNode => {
    const percentage = leakageUsagePercentage(row);
    const overcount = (row.varianceQty ?? 0) > 0;
    return (
      <div className="mx-auto flex min-w-[108px] max-w-[160px] flex-col gap-1.5" title={t('inventory.leakage.usageVarianceTooltip')}>
        <div className="flex items-center justify-end gap-2 whitespace-nowrap">
          <span className={cn(smallTextSize, primaryText)}>{qtyFormat.row(row, row.varianceQty)}</span>
          <span className={cn('text-xs', tertiaryText)}>{percentage === null ? '—' : `${Number(percentage.toFixed(1))}%`}</span>
        </div>
        {percentage !== null && (
          <div className="h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700" aria-hidden="true">
            <div className={cn('h-full rounded-full', overcount ? 'bg-emerald-500 dark:bg-emerald-400' : 'bg-red-400 dark:bg-red-400')}
              style={{ width: `${Math.min(Math.abs(percentage), 100)}%` }} />
          </div>
        )}
      </div>
    );
  };
  const renderPoolWarnings = (row: LeakageRow): ReactNode => {
    const uncounted = row.pool?.membersUncounted ?? [];
    const excluded = row.pool?.membersExcluded ?? [];
    const elsewhere = row.pool?.membersInOtherGroups ?? [];
    if (!uncounted.length && !excluded.length && !elsewhere.length) return null;
    return (
      <div className="mt-1 flex flex-wrap items-center gap-1">

        {uncounted.length > 0 && (
          // The asymmetry that creates phantom leakage: their purchases are in this row
          // while their stock is unknown. Worth naming on the row, not just in the drawer.
          <span
            className={grayBadge}
            title={t('inventory.leakage.pool.uncountedHint', { names: uncounted.join(', ') })}
          >
            {t('inventory.leakage.pool.uncounted', { count: uncounted.length })}
          </span>
        )}
        {excluded.length > 0 && (
          // A different failure: nothing of theirs is in these numbers at all, so the row
          // under-reports rather than over-reports. Never merge the two counts.
          <span
            className={grayBadge}
            title={t('inventory.leakage.pool.excludedHint', { names: excluded.join(', ') })}
          >
            {t('inventory.leakage.pool.excluded', { count: excluded.length })}
          </span>
        )}
        {elsewhere.length > 0 && (
          // Not a failure at all, which is why it gets its own badge rather than joining
          // the two above: these members ARE counted, just in another inventory, and that
          // group's own pool row reconciles them over its own window.
          <span
            className={grayBadge}
            title={t('inventory.leakage.pool.otherGroupsHint', { names: elsewhere.join(', ') })}
          >
            {t('inventory.leakage.pool.otherGroups', { count: elsewhere.length })}
          </span>
        )}
      </div>
    );
  };

  const productColumns = (): any[] => [
    {
      key: 'productName',
      label: t('inventory.product'),
      sortable: true,
      sortKey: 'productName',
      render: (_v: unknown, it: LeakageDisplayRow) => {
        if (it.kind === 'member') {
          const { member } = it;
          return (
            <div className="flex min-w-0 flex-col pl-8">
              <span className={cn(smallTextSize, mediumText, member.counted ? primaryText : tertiaryText)}>
                {member.productName}
              </span>
              <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                <span
                  className={cn(
                    'inline-flex w-fit items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                    member.isPrimary
                      ? cn(canvasBg, primaryText)
                      : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300'
                  )}
                >
                  {t(member.isPrimary ? 'inventory.leakage.pool.primary' : 'inventory.leakage.pool.alternative')}
                </span>
                {!member.counted && (
                  <span
                    className={grayBadge}
                    title={t(member.inert ? 'inventory.leakage.pool.inertHint' : 'inventory.leakage.pool.notCountedHint')}
                  >
                    {t(member.inert ? 'inventory.leakage.pool.inert' : 'inventory.leakage.pool.notCounted')}
                  </span>
                )}
                {/* Platform is '' when the recipe row carried none — skip rather than
                    resolve a logo for a supplier we cannot name. */}
                {member.platform && (
                  <SupplierLogo
                    platform={Platform.fromString(member.platform)}
                    unknownSupplierName={member.unknownSupplierName}
                    size="small"
                    alt={member.unknownSupplierName ?? member.platform}
                  />
                )}
              </div>
            </div>
          );
        }
        const { row } = it;
        const pool = row.pool;
        return (
          <div className="flex items-center gap-2">
            <FavoriteStar isFavorite={isRowFavorite(row, favoriteKeys)} onToggle={() => handleToggleFavorite(row)} />
            {pool ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedPools((prev) => {
                    const next = new Set(prev);
                    if (!next.delete(row.key)) next.add(row.key);
                    return next;
                  });
                }}
                aria-expanded={expandedPools.has(row.key)}
                aria-label={t('inventory.leakage.pool.toggleMembers')}
                className="shrink-0 rounded p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <ChevronDown
                  className={cn('h-4 w-4 transition-transform', tertiaryText, !expandedPools.has(row.key) && '-rotate-90')}
                />
              </button>
            ) : null}
            <div className="flex min-w-0 flex-col">
              <span className={cn(primaryText, smallTextSize, semiBoldText)}>
                {row.groupName ? `${row.groupName} · ` : ''}
                {row.productName}
              </span>
              {renderPoolWarnings(row)}
              {pool && (
                <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                  <span
                    className="inline-flex w-fit items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-600 dark:bg-blue-900/20 dark:text-blue-300"
                    // The badge counts the CONFIGURED pool, but members counted in another
                    // inventory reconcile there — so the sub-rows can be fewer than this says.
                    title={
                      (pool.membersInOtherGroups ?? []).length > 0
                        ? t('inventory.leakage.pool.otherGroupsHint', { names: (pool.membersInOtherGroups ?? []).join(', ') })
                        : undefined
                    }
                  >
                    <ArrowLeftRight className="h-3 w-3" />
                    {t('inventory.leakage.pool.badge', { count: pool.memberCount - 1 })}
                  </span>
                  {(row.members ?? [])
                    .filter((m) => m.platform)
                    .slice(0, 3)
                    .map((m) => (
                      <SupplierLogo
                        key={m.key}
                        platform={Platform.fromString(m.platform)}
                        unknownSupplierName={m.unknownSupplierName}
                        size="small"
                        alt={m.unknownSupplierName ?? m.platform}
                      />
                    ))}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'startQty',
      label: t('inventory.leakage.colOpening'),
      sortable: true,
      sortKey: 'startQty',
      render: (_v: unknown, it: LeakageDisplayRow) => (
        <span className={cn(secondaryText, smallTextSize)}>
          {it.kind === 'member' ? qtyFormat.member(it.member, it.member.startQty) : qtyFormat.row(it.row, it.row.startQty)}
        </span>
      ),
    },
    {
      key: 'purchasedQty',
      label: t('inventory.leakage.colPurchased'),
      sortable: true,
      sortKey: 'purchasedQty',
      render: (_v: unknown, it: LeakageDisplayRow) => (
        <span className={cn(secondaryText, smallTextSize)}>
          {it.kind === 'member'
            ? qtyFormat.member(it.member, it.member.purchasedQty)
            : qtyFormat.row(it.row, it.row.purchasedQty)}
        </span>
      ),
    },
    {
      key: 'neededQty',
      label: t('inventory.leakage.colUsage'),
      sortable: true,
      sortKey: 'neededQty',
      render: (_v: unknown, it: LeakageDisplayRow) =>
        it.kind === 'member' ? (
          // Allocated demand is a supply-proportional split, never an observation — say so
          // rather than letting it read like a counted fact.
          <span className={cn(secondaryText, smallTextSize)} title={t('inventory.leakage.pool.estimateHint')}>
            ≈ {qtyFormat.member(it.member, it.member.allocatedNeededQty)}
          </span>
        ) : (
          <span className={cn(secondaryText, smallTextSize)}>{qtyFormat.row(it.row, it.row.neededQty)}</span>
        ),
    },
    {
      key: 'expectedQty',
      label: t('inventory.leakage.colExpectedQty'),
      sortable: true,
      sortKey: 'expectedQty',
      render: (_v: unknown, it: LeakageDisplayRow) =>
        it.kind === 'member' ? (
          memberDash
        ) : (
          <span className={cn(secondaryText, smallTextSize)}>{qtyFormat.row(it.row, it.row.expectedQty)}</span>
        ),
    },
    {
      key: 'endQty',
      label: t('inventory.leakage.colCountedQty'),
      sortable: true,
      sortKey: 'endQty',
      render: (_v: unknown, it: LeakageDisplayRow) => (
        <span className={cn(secondaryText, smallTextSize)}>
          {it.kind === 'member' ? qtyFormat.member(it.member, it.member.endQty) : qtyFormat.row(it.row, it.row.endQty)}
        </span>
      ),
    },
    {
      key: 'varianceQty',
      label: t('inventory.leakage.colLeakageQty'),
      sortable: true,
      sortKey: 'varianceQty',
      render: (_v: unknown, it: LeakageDisplayRow) =>
        it.kind === 'member' ? (
          memberDash
        ) : (
          renderLeakageQuantity(it.row)
        ),
    },
    {
      key: 'varianceValue',
      label: t('inventory.leakage.colLeakageEur'),
      sortable: true,
      sortKey: 'varianceValue',
      render: (_v: unknown, it: LeakageDisplayRow) => (it.kind === 'member' ? memberDash : renderEur(t, it.row.varianceValue)),
    },
  ];

  // Direction tabs with live counts, computed on the rows the other filters already kept.
  // The Favorites tab only exists once something is starred (star a row in All to create it).
  const dirDefs: { key: DirFilter; label: string; count: number }[] = [
    ...(hasFavorites
      ? [{
          key: 'favorites' as DirFilter,
          label: t('inventory.leakage.dir.favorites'),
          count: baseRows.filter((r) => matchesDir(r, 'favorites', favoriteKeys)).length,
        }]
      : []),
    { key: 'all', label: t('inventory.leakage.dir.all'), count: baseRows.length },
    { key: 'negative', label: t('inventory.leakage.dir.negative'), count: baseRows.filter((r) => matchesDir(r, 'negative', favoriteKeys)).length },
    { key: 'positive', label: t('inventory.leakage.dir.positive'), count: baseRows.filter((r) => matchesDir(r, 'positive', favoriteKeys)).length },
    { key: 'highRisk', label: t('inventory.leakage.dir.highRisk'), count: baseRows.filter((r) => matchesDir(r, 'highRisk', favoriteKeys)).length },
  ];
  const productSearch = (
    <div className="relative min-w-0 flex-1 sm:max-w-[250px]">
      <Search className={cn('absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2', tertiaryText)} />
      <input type="search" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
        aria-label={t('inventory.searchProducts')} placeholder={t('inventory.searchProducts')}
        className={cn('h-10 w-full border pl-9 pr-3 focus:outline-none focus:ring-1 focus:ring-darkGreenColor dark:focus:ring-darkGreenColor-dark', rounded, smallTextSize, cardBg, borderPrimary, primaryText, placeholderPrimary)} />
    </div>
  );
  const productControls = (
    <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between">
      <div className={cn('hide-scrollbar flex w-fit max-w-full overflow-x-auto rounded-xl border', borderPrimary)}>
        {dirDefs.map((def) => {
          const active = dirFilter === def.key;
          return (
            <button key={def.key} type="button" onClick={() => setPickedDir(def.key)} aria-pressed={active}
              data-onboarding={`inventory-leakage-dir-${def.key}`}
              className={cn('flex shrink-0 items-center gap-2 whitespace-nowrap border-r px-3 py-2 last:border-r-0', borderPrimary, smallTextSize,
                active ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300' : secondaryText)}>
              {def.label}<span className={cn('rounded-full border px-1.5 text-[10px]', borderPrimary)}>{def.count}</span>
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative" ref={filtersRef}>
          <button type="button" aria-expanded={filtersOpen} aria-controls="leakage-table-filters"
            onClick={() => setFiltersOpen((open) => !open)}
            className={cn('flex h-10 items-center gap-2 rounded-xl border px-3', borderPrimary, smallTextSize, secondaryText)}>
            <ListFilter className="h-4 w-4" />{t('common.filters')}{activeFilterCount > 0 && <span>({activeFilterCount})</span>}
          </button>
          {filtersOpen && (
            <div id="leakage-table-filters" className={cn('absolute left-0 top-full z-30 mt-2 flex w-72 max-w-[calc(100vw-3rem)] flex-col gap-4 rounded-2xl border p-4 shadow-lg sm:left-auto sm:right-0', cardBg, borderPrimary)}>
              <label className={cn('flex flex-col gap-2', smallTextSize, secondaryText)}>
                {t('inventory.leakage.colConfidence')}
                <select className={cn(selectCls, 'w-full')} value={confFilter} onChange={(e) => setConfFilter(e.target.value as typeof confFilter)}>
                  <option value="all">{t('inventory.leakage.confFilter.all')}</option>
                  {(['high', 'medium', 'low'] as const).map((value) => <option key={value} value={value}>{t(`inventory.leakage.confidence.${value}`)}</option>)}
                </select>
              </label>
              <label className={cn('flex flex-col gap-2', smallTextSize, secondaryText)}>
                {t('inventory.leakage.inventoryCategoryFilter')}
                <select className={cn(selectCls, 'w-full')} value={catFilter} onChange={(e) => setCatFilter(e.target.value)}>
                  <option value="all">{t('inventory.leakage.catAll')}</option>
                  {categoryFilterOptions.map((category) => <option key={category} value={category}>{category}</option>)}
                </select>
              </label>
              <label className={cn('flex flex-col gap-2', smallTextSize, secondaryText)}>
                {t('inventory.leakage.productCategoryFilter')}
                <select className={cn(selectCls, 'w-full')} value={productCatFilter} onChange={(e) => setProductCatFilter(e.target.value)}>
                  <option value="all">{t('inventory.leakage.catAll')}</option>
                  {productCategoryFilterOptions.map((category) => <option key={category} value={category}>{category}</option>)}
                </select>
              </label>
              <button type="button" onClick={clearTableFilters} className={cn('self-start rounded-xl px-4 py-2', canvasBg, primaryText, semiBoldText)}>{t('common.clearAll')}</button>
            </div>
          )}
        </div>
        <TabbedSections isTimeOptionSelection={false} value={unitMode} onboardingId="inventory-leakage-unit"
          onChange={(v) => {
            if (practiceActive || getInventoryPracticeDataset()) setPracticeUnitMode(v as LeakageUnitMode);
            else void saveLeakageQtyUnitSetting(v as LeakageUnitMode);
          }}
          options={[
            { value: 'std', label: t('inventory.leakage.unitModeStd') },
            { value: 'count', label: t('inventory.leakage.unitModeCount') },
          ]} />
      </div>
    </div>
  );

  // Category card toggle (same control as the p2 "Value by category" card).
  const categoryToggle = (
    <TabbedSections
      options={[
        { value: 'inventory', label: t('inventory.leakage.catModeInventory') },
        { value: 'product', label: t('inventory.leakage.catModeProduct') },
      ]}
      value={categoryMode}
      onChange={(v) => setCategoryMode(v as CategoryMode)}
      isTimeOptionSelection={true}
    />
  );

  // --- breakdown charts: the top bars per dimension (the full tables live in the slide-overs) ---
  const barSubline = (count: number): ReactNode => (
    <span className={tertiaryText}>{t('inventory.productCountLabel', { count })}</span>
  );
  // Products per section, counted the same way the variance attributes them, so the
  // subline matches the bar it sits on (mixed/unattributable rows land on the rest bucket).
  const sectionProductCounts = new Map<string, number>();
  view.rows.forEach((r) => {
    const key = r.attributedSectionKey ?? UNATTRIBUTED_KEY;
    sectionProductCounts.set(key, (sectionProductCounts.get(key) ?? 0) + 1);
  });
  const sectionBars = buildLeakageBars(
    view.sections.map((s) => ({
      id: s.sectionKey,
      label:
        s.sectionKey === UNATTRIBUTED_KEY
          ? t('inventory.leakage.unattributed')
          : `${s.sectionName}${s.deleted ? ` ${t('inventory.leakage.deletedSection')}` : ''}`,
      value: s.varianceValue,
      // The movement pill only earns its place when it says something the leakage number doesn't.
      changeValue: Math.abs(s.changeValue - s.varianceValue) > 0.005 ? s.changeValue : null,
      productCount: sectionProductCounts.get(s.sectionKey) ?? 0,
      muted: s.sectionKey === UNATTRIBUTED_KEY,
    })),
    barSubline,
    () => setSectionsSlideOpen(true)
  );
  const catBars = buildLeakageBars(
    catRows.map((c) => ({ id: c.name, label: c.name, value: c.varianceValue, productCount: c.productCount })),
    barSubline,
    () => setCatSlideOpen(true)
  );

  // Section / category breakdown columns (shared by the top-10 card and its expand modal).
  const sectionColumns: TableComponentProps<VarianceSectionRow>['columns'] = [
    {
      key: 'sectionName',
      label: t('inventory.leakage.colSection'),
      render: (_v, row) => {
        const isUnattr = row.sectionKey === UNATTRIBUTED_KEY;
        const diff = Math.abs(row.changeValue - row.varianceValue) > 0.005;
        return (
          <div>
            <span className={cn(smallTextSize, semiBoldText, isUnattr ? cn(tertiaryText, 'italic') : primaryText)}>
              {isUnattr ? t('inventory.leakage.unattributed') : row.sectionName}
              {row.deleted && <span className={cn(tertiaryText, 'ml-1')}>{t('inventory.leakage.deletedSection')}</span>}
            </span>
            {!isUnattr && diff && (
              <p className={cn(smallTextSize, tertiaryText)}>
                {t('inventory.leakage.changeLine', { value: money(row.changeValue) })}
              </p>
            )}
          </div>
        );
      },
    },
    {
      key: 'expectedValue',
      label: t('inventory.leakage.colExpected'),
      render: (_v, row) => <span className={cn(secondaryText, smallTextSize)}>{money(row.expectedValue)}</span>,
    },
    {
      key: 'countedValue',
      label: t('inventory.leakage.colCounted'),
      render: (_v, row) => <span className={cn(secondaryText, smallTextSize)}>{money(row.countedValue)}</span>,
    },
    {
      key: 'varianceValue',
      label: t('inventory.leakage.colLeakage'),
      render: (_v, row) => <span className={cn(smallTextSize, semiBoldText, eurTone(row.varianceValue))}>{money(row.varianceValue)}</span>,
    },
    {
      key: 'variancePct',
      label: t('inventory.leakage.colPct'),
      render: (_v, row) => <span className={cn(secondaryText, smallTextSize)}>{pctText(row.variancePct)}</span>,
    },
  ];

  const catColumns: TableComponentProps<LeakageCatRow>['columns'] = [
    {
      key: 'name',
      label: t('inventory.leakage.colCategory'),
      render: (_v, row) => <span className={cn(primaryText, smallTextSize, semiBoldText)}>{row.name}</span>,
    },
    {
      key: 'expectedValue',
      label: t('inventory.leakage.colExpected'),
      render: (_v, row) => <span className={cn(secondaryText, smallTextSize)}>{money(row.expectedValue)}</span>,
    },
    {
      key: 'countedValue',
      label: t('inventory.leakage.colCounted'),
      render: (_v, row) => <span className={cn(secondaryText, smallTextSize)}>{money(row.countedValue)}</span>,
    },
    {
      key: 'varianceValue',
      label: t('inventory.leakage.colLeakage'),
      render: (_v, row) => <span className={cn(smallTextSize, semiBoldText, eurTone(row.varianceValue))}>{money(row.varianceValue)}</span>,
    },
    {
      key: 'variancePct',
      label: t('inventory.leakage.colPct'),
      render: (_v, row) => <span className={cn(secondaryText, smallTextSize)}>{pctText(row.variancePct)}</span>,
    },
  ];

  return (
    <PracticeScopeProvider value={practiceActive}>
    <div>
      {view.unmeasurableProductNames.length > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
          <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <p className={cn(smallTextSize, 'text-amber-800 dark:text-amber-300')}>
            {t('inventory.leakage.unmeasurableNote', {
              count: view.unmeasurableProductNames.length,
              products: view.unmeasurableProductNames.join(', '),
            })}
          </p>
        </div>
      )}

      {/* KPI row — 2-col compact grid on phones */}
      <div className="mb-6 grid grid-cols-2 gap-2.5 sm:gap-4 lg:grid-cols-4">
        <div className="h-full min-w-0" data-onboarding="inventory-leakage-kpis inventory-leakage-kpi-expected">
        <StatCard
          icon={<Scale className="w-6 h-6" style={{ color: '#2E90FA' }} />}
          iconBg={iconBgBlue}
          mobileLayout="tile"
          title={t('inventory.leakage.kpiExpected')}
          value={totals.expectedTotalValue}
          valueType="currency"
          subtitle={t('inventory.leakage.kpiExpectedSub')}
        />
        </div>
        <div className="h-full min-w-0" data-onboarding="inventory-leakage-kpis inventory-leakage-kpi-counted">
        <StatCard
          icon={<ClipboardCheck className="w-6 h-6" style={{ color: '#079455' }} />}
          iconBg={iconBgGreen}
          mobileLayout="tile"
          title={t('inventory.leakage.kpiCounted')}
          value={totals.countedTotalValue}
          valueType="currency"
          subtitle={countedSub}
        />
        </div>
        <div className="h-full min-w-0" data-onboarding="inventory-leakage-kpis inventory-leakage-kpi-leakage">
        <StatCard
          icon={<TrendingDown className="w-6 h-6" style={{ color: '#D92D20' }} />}
          iconBg={iconBgRed}
          mobileLayout="tile"
          title={t('inventory.leakage.kpiLeakage')}
          value={totals.varianceTotalValue}
          valueType="currency"
          valueClassName={totals.varianceTotalValue < 0 ? redTextColor : undefined}
        />
        </div>
        <div className="h-full min-w-0" data-onboarding="inventory-leakage-kpis inventory-leakage-kpi-confidence">
        <StatCard
          icon={<ShieldCheck className="w-6 h-6" style={{ color: '#6938EF' }} />}
          iconBg={iconBgPurple}
          mobileLayout="tile"
          title={t('inventory.leakage.kpiConfidence')}
          value={totals.confidenceScore * 100}
          valueType="percentage"
          subtitle={t('inventory.leakage.kpiConfidenceSub', {
            linked: totals.linkedProductCount,
            total: totals.totalProductCount,
          })}
        />
        </div>
      </div>

      {/* Products — the centerpiece: one full table, its own scope control on top */}
      <div className={cn('mt-6 p-4', cardBg, rounded, softCard)} data-onboarding="inventory-leakage-table">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <h3 className={cn(largeTextSize, semiBoldText, primaryText)}>{t('inventory.leakage.productsTitle')}</h3>
            <span
              className={cn(
                'flex h-6 min-w-[24px] items-center justify-center rounded-full border px-1.5 text-xs font-medium',
                canvasBg,
                tertiaryText,
                borderPrimary
              )}
            >
              {sortedRows.length}
            </span>
          </div>
          <div className="flex min-w-0 items-center gap-2 sm:justify-end">
          {productSearch}
          {canExport && (
            <div className="relative flex-none">
              <button
                type="button"
                onClick={() => setExportMenuOpen((o) => !o)}
                disabled={sortedRows.length === 0}
                aria-haspopup="menu"
                aria-label={t('inventory.leakage.exportCsv')}
                aria-expanded={exportMenuOpen}
                className={cn(
                  'inline-flex h-11 shrink-0 items-center justify-center gap-2 border border-darkGreenColor px-4 dark:border-darkGreenColor-dark',
                  'text-darkGreenColor dark:text-darkGreenColor-dark hover:bg-backgroundDarker dark:hover:bg-backgroundDarker-dark',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                  rounded,
                  smallTextSize,
                  semiBoldText,
                  cardBg
                )}
              >
                <Download className="h-5 w-5" />
                <span className="hidden sm:inline">{t('inventory.leakage.exportCsv')}</span>
                {exportMenuOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              {exportMenuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setExportMenuOpen(false)} />
                  <div
                    role="menu"
                    className={cn(
                      'absolute right-0 mt-2 w-72 max-w-[calc(100vw-2rem)] z-20 border shadow-lg overflow-hidden',
                      borderPrimary,
                      cardBg,
                      rounded
                    )}
                  >
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setExportMenuOpen(false);
                        handleExportCsv();
                      }}
                      className={cn(
                        'w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800',
                        smallTextSize,
                        semiBoldText,
                        primaryText
                      )}
                    >
                      <span className="block">{t('inventory.leakage.exportCsvSimple')}</span>
                      <span className={cn('block font-normal', tertiaryText)}>
                        {t('inventory.leakage.exportCsvSimpleHint')}
                      </span>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setExportMenuOpen(false);
                        handleExportDetailedCsv();
                      }}
                      className={cn(
                        'w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800',
                        smallTextSize,
                        semiBoldText,
                        primaryText
                      )}
                    >
                      <span className="block">{t('inventory.leakage.exportCsvDetailed')}</span>
                      <span className={cn('block font-normal', tertiaryText)}>
                        {t('inventory.leakage.exportCsvDetailedHint')}
                      </span>
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          </div>
        </div>
        <div className="mt-3">{productControls}</div>
        <ResponsiveTable desktop={<TableComponent<LeakageDisplayRow>
          key={practiceActive ? 'practice' : 'restaurant'}
          initialPage={practiceActive ? 1 : productPage}
          onPageChange={(page) => { if (!practiceActive) setProductPage(page); }}
          itemsPerPage={practiceActive ? 50 : undefined}
          title=""
          className="p-0 pt-4"
          data={displayRows}
          columns={productColumns()}
          usePagination={!practiceActive}
          blankLines={false}
          options={{ noBlankPadding: true }}
          // Only a reconciled row opens the drawer — a member has no variance of its own.
          isRowClickable={(it) => it.kind === 'row'}
          onRowClick={(it) => it.kind === 'row' && setSelectedUid(rowUid(it.row))}
          rowOnboardingId={(it) => it.kind === 'row' && rowUid(it.row) === leakageWalkthroughRowUid() ? 'inventory-leakage-row' : undefined}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={handleSort}
        />} mobile={
          <MobileCardList items={displayRows.slice(0, mobileLimit)}
            keyOf={(it) => it.kind === 'row' ? rowUid(it.row) : `${rowUid(it.parent)}:${it.member.key}`}
            onLoadMore={() => setMobileLimit((limit) => limit + 20)} hasMore={displayRows.length > mobileLimit}
            loadMoreLabel={t('common.loadMore')} className="mt-4"
            renderCard={(it) => {
              const columns = productColumns();
              return <div className={cn('rounded-xl border p-3', borderPrimary)}>
                {columns[0].render(undefined, it)}
                <button type="button" className="mt-3 grid w-full grid-cols-2 gap-3 text-left" disabled={it.kind !== 'row'}
                  onClick={() => it.kind === 'row' && setSelectedUid(rowUid(it.row))}>
                  {columns.slice(1).map((column) => <MobileCardField key={column.key} label={column.label} value={column.render(undefined, it)} />)}
                </button>
              </div>;
            }} />
        } />
        {displayRows.length === 0 && <p className={cn('py-6 text-center sm:hidden', secondaryText, smallTextSize)}>{t('common.noDataInPeriod')}</p>}
      </div>

      {/* Breakdown charts — where the leakage sits, by category and by section */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <CardWithHeader
          title={t('inventory.leakage.categoryCardTitle')}
          onboardingId="inventory-leakage-categories"
          className={softCard}
          headerActions={categoryToggle}
          seeAllText={catRows.length > 0 ? t('dashboard.seeAll') : undefined}
          onSeeAllClick={catRows.length > 0 ? () => setCatSlideOpen(true) : undefined}
        >
          <RankedValueBars rows={catBars} barColor="green" variant="chart" emptyText={t('common.noDataInPeriod')} />
        </CardWithHeader>

        <CardWithHeader
          title={t('inventory.leakage.sectionCardTitle')}
          onboardingId="inventory-leakage-sections"
          className={softCard}
          seeAllText={view.sections.length > 0 ? t('dashboard.seeAll') : undefined}
          onSeeAllClick={view.sections.length > 0 ? () => setSectionsSlideOpen(true) : undefined}
        >
          <RankedValueBars rows={sectionBars} barColor="green" variant="chart" emptyText={t('common.noDataInPeriod')} />
          <p className={cn(smallTextSize, tertiaryText)}>
            {t('inventory.leakage.unattributedNote', {
              value: money(Math.abs(view.unattributedVarianceValue)),
              total: money(Math.abs(view.varianceTotalValue)),
              pct: `${(view.unattributedShare * 100).toFixed(0)}%`,
            })}
          </p>
        </CardWithHeader>
      </div>

      {/* Full breakdown tables, one slide-over per chart */}
      {sectionsSlideOpen && (
        <SlideOverShell onClose={() => setSectionsSlideOpen(false)} widthClass="max-w-[900px]">
          <TableComponent
            title={t('inventory.leakage.sectionCardTitle')}
            className="p-0"
            data={view.sections}
            columns={sectionColumns}
            usePagination={true}
            blankLines={false}
            options={{ noBlankPadding: true }}
          />
        </SlideOverShell>
      )}
      {catSlideOpen && (
        <SlideOverShell onClose={() => setCatSlideOpen(false)} widthClass="max-w-[900px]">
          <TableComponent
            title={t('inventory.leakage.categoryCardTitle')}
            className="p-0"
            headerActions={categoryToggle}
            data={catRows}
            columns={catColumns}
            usePagination={true}
            blankLines={false}
            options={{ noBlankPadding: true }}
          />
        </SlideOverShell>
      )}

      {/* Custom ingredients strip — only when non-empty */}
      {view.customItems.length > 0 && (
        <div className="mt-6">
          <TableComponent
            title={t('inventory.leakage.customTitle')}
            className={softCard}
            data={view.customItems}
            columns={[
              {
                key: 'productName',
                label: t('inventory.product'),
                render: (_v, row) => <span className={cn(primaryText, smallTextSize, semiBoldText)}>{row.productName}</span>,
              },
              // A custom ingredient counted in two unbridgeable unit spaces has no delta to
              // take, and the engine sends null rather than a 0 — the formatter's `custom`
              // is what keeps that off the screen as a dash instead of "0.00 kg".
              {
                key: 'startQty',
                label: t('inventory.leakage.colStart'),
                render: (_v, row) => <span className={cn(secondaryText, smallTextSize)}>{qtyFormat.custom(row, row.startQty)}</span>,
              },
              {
                key: 'endQty',
                label: t('inventory.leakage.colEnd'),
                render: (_v, row) => <span className={cn(secondaryText, smallTextSize)}>{qtyFormat.custom(row, row.endQty)}</span>,
              },
              {
                key: 'deltaQty',
                label: t('inventory.leakage.colChange'),
                render: (_v, row) => (
                  <span className={cn(smallTextSize, semiBoldText, (row.deltaQty ?? 0) < 0 ? redTextColor : greenTextColor)}>
                    {qtyFormat.custom(row, row.deltaQty)}
                    {row.deltaEur !== null && <span className={cn(tertiaryText, 'ml-1')}>({money(row.deltaEur)})</span>}
                  </span>
                ),
              },
            ]}
            itemsPerPage={50}
            blankLines={false}
          />
          <p className={cn(smallTextSize, tertiaryText, 'mt-2 px-1')}>{t('inventory.leakage.customCaption')}</p>
        </div>
      )}

      {/* Product detail drawer (row click) — the page owns every modal its actions open, so
          they survive the drawer re-rendering and stack above its z-50 chrome. */}
      <LeakageProductDrawer
        row={selectedRow}
        usage={selectedUsage}
        qtyFormat={qtyFormat}
        periodLabel={selectedWindow ? `${selectedWindow.startLabel} \u2192 ${selectedWindow.endLabel}` : undefined}
        // A pooled row's primary member carries its own supplier identity, so read it straight
        // off the member rather than re-deriving one from order history.
        supplier={selectedRow ? drawerSupplier(selectedRow) : null}
        liveCategory={selectedRow ? liveCategoryByKey.get(anchorKeyOf(selectedRow)) ?? null : null}
        linkedProduct={selectedLinked}
        onClose={() => setSelectedUid(null)}
        onEditDish={setEditCostPriceId}
        onAddLink={selectedRow ? () => setSetupRow(selectedRow) : undefined}
        onEditInventoryLink={selectedRelink ? () => setRelinkTarget(selectedRelink) : undefined}
        // Each of these renders its own dim + panel shadow on top of the drawer, so the
        // drawer drops its own (they doubled up) and stops answering Escape.
        overlayOpen={!!editCostPriceId || !!setupRow || !!relinkTarget}
      />

      {/* Cost-price editor for one dish of the drawer's usage breakdown. The wrapper's z-index
          is load-bearing: the editor's own slide-over chrome is z-30, below the drawer's z-50,
          so it needs its own stacking context to sit on top. */}
      {editCostPriceId && (
        <div className="relative z-[60]">
          <CostPriceDetailModal
            costPriceId={editCostPriceId}
            onClose={() => setEditCostPriceId(null)}
            onBack={() => setEditCostPriceId(null)}
            defaultWastePercentage={defaultWastePercentage}
            initialFocusProductKey={selectedIdentity ? productKey(selectedIdentity.platform, selectedIdentity.articleId, selectedIdentity.productName) : undefined}
          />
        </div>
      )}

      {/* Leakage rows are keyed by inventory key, so the setup flow matches on `inventory`. */}
      {setupRow && (
        <div className="relative z-[60]">
          <ProductSetupModal
            practice={practiceActive}
            identity={{
              platform: liveByKey.supplier.get(anchorKeyOf(setupRow))?.platform ?? '',
              productName: setupRow.productName,
              match: { kind: 'inventory', inventoryKey: anchorKeyOf(setupRow) },
            }}
            restaurantId={restaurantId}
            onClose={() => setSetupRow(null)}
          />
        </div>
      )}

      {/* Re-point the counted row at another order product. The relink rewrites the count's
          frozen key, so the counts have to be refetched for the drawer to re-derive. */}
      {relinkTarget && (
        <div className="relative z-[60]">
          <ReLinkInventoryProductModal
            product={relinkTarget}
            restaurantId={restaurantId}
            onClose={() => {
              setRelinkTarget(null);
              if (!practiceActive) void loadCountsHistory(restaurantId);
            }}
          />
        </div>
      )}
    </div>
    </PracticeScopeProvider>
  );
};

export default LeakageTab;
